
import {Component, Prop, Vue} from 'vue-property-decorator'
import moment, {DurationInputArg2, Moment} from 'moment-timezone'

export interface VDatePickerAttribute {
  dates: Date
  dot?: {
    class: string
  }
  highlight?: {
    class: string
  }
}

/**
 * This is a wrapper component for the 3rd-party
 * v-calendar's DatePicker component
 */
@Component({ name: 'DatePicker' })
export default class extends Vue {
  // Note: use `selectedDate` getter instead of using this directly
  @Prop({ required: true })
  selected: Moment | null

  @Prop({ default: () => () => [] })
  // eslint-disable-next-line no-unused-vars
  getDateAnnotations: (date: Moment) => MessageAction[]

  @Prop({ required: true })
  // eslint-disable-next-line no-unused-vars
  getDateStatus: (date: Moment) => DateAvailabilityStatus

  // Use callback instead of `$emit` because `$emit` doesn't work when used a modal component
  @Prop({ required: true })
  // eslint-disable-next-line no-unused-vars
  onSelect: (date: Moment) => void

  @Prop({ default: () => moment().subtract(99, 'year') })
  minDate: Moment

  @Prop({ default: () => moment().add(99, 'year') })
  maxDate: Moment

  @Prop({ required: false })
  columns: number

  /**
   * These are used for creating annotations (dots and highlights).
   * Instead of creating annotations from `minDat` to `maxDate` which
   * can have a performance impact, we only create annotations for visible dates only.
   *
   * These will be set by `@update:from-page` and `@update:to-page` events.
   */
  visibleFromDate: number = moment().startOf('month').valueOf()
  visibleToDate: number = moment().endOf('month').valueOf()

  focusedDate: Date | null = null

  mounted() {
    this.makeAccessible()
  }

  get selectedDate(): Date | null {
    return this.selected ? moment(this.selected.format('YYYY-MM-DD')).toDate() : null
  }

  makeAccessible() {
    const datePicker = this.$refs.datePicker as any

    const today = new Date()
    let dateToFocus = this.selectedDate ?? (this.minDate.isAfter(today) ? this.minDate.toDate() : today)
    datePicker.focusDate(dateToFocus)

    const nextMonthButton = datePicker.$el.querySelector('.vc-arrow.is-right') as HTMLElement
    nextMonthButton.setAttribute('aria-label', 'Next month')
    nextMonthButton.setAttribute('tabindex', '0')

    const prevMonthButton = datePicker.$el.querySelector('.vc-arrow.is-left') as HTMLElement
    prevMonthButton.setAttribute('aria-label', 'Previous month')
    prevMonthButton.setAttribute('tabindex', '0')

    this.makeKeyboardAccessible()
  }

  private makeKeyboardAccessible() {
    const datePicker = this.$refs.datePicker as Vue
    datePicker.$el.addEventListener('keydown', (e: any) => {
      const day = moment(this.focusedDate ?? this.selectedDate ?? new Date())
      let moveBy: number = 0
      let moveUnit: DurationInputArg2 = 'day'

      switch (e.code) {
        case 'ArrowLeft':
          moveBy = -1
          moveUnit = 'day'
          break
        case 'ArrowRight':
          moveBy = 1
          moveUnit = 'day'
          break
        case 'ArrowUp':
          moveBy = -1
          moveUnit = 'week'
          break
        case 'ArrowDown':
          moveBy = 1
          moveUnit = 'week'
          break
        default:
          return
      }

      day.add(moveBy, moveUnit)
      if (day.isBetween(this.minDate, this.maxDate)) {
        this.focusedDate = day.toDate()
        // @ts-ignore
        datePicker.focusDate(this.focusedDate)
      }
    })
  }

  get displayedDates(): Moment[] {
    const dates: Moment[] = []
    // Two calendar months are always shown on desktop. Pad the visible dates by one month to each side to handle events that only span one calendar month.
    const firstVisibleDate = moment(this.visibleFromDate).subtract(1, 'month')
    const lastVisibleDate = moment(this.visibleToDate).add(1, 'month')

    for (const curr = firstVisibleDate; curr.isBefore(lastVisibleDate); curr.add(1, 'day')) {
      // Note: it's important to clone the moment date here.
      dates.push(curr.clone())
    }
    return dates
  }

  /**
   * Specifies styles/highlights (classnames) and annotations/dots for specific dates.
   *
   * @see https://vcalendar.io/attributes.html
   */
  get vCalendarAttributes(): VDatePickerAttribute[] {
    const result: VDatePickerAttribute[] = []

    // Always annotate today.
    result.push({
      // TODO Use today in the TZ of the _venue_.
      dates: moment().toDate(),
      highlight: { class: 'is-today' },
    })

    // Highlight the selected date.
    if (this.selectedDate) {
      result.push({
        dates: this.selectedDate,
        highlight: { class: 'selected' },
      })
    }

    // Add a dot for each annotation.
    for (const date of this.displayedDates) {
      for (const annotation of this.getDateAnnotations(date)) {
        result.push({
          dates: date.toDate(),
          dot: { class: annotation.style },
        })
      }
    }

    return result
  }

  isSoldOut(date: Moment): boolean {
    return 'sold_out' == this.getDateStatus(date)
  }

  getDayContentClasses(dDate: Date) {
    // TODO Do we need to specify TZ here?
    const date = moment(dDate)
    const result: string[] = []

    if (this.isDateSelected(date)) {
      result.push('selected')
    }

    const status = this.getDateStatus(date)
    result.push(status)

    if (status != 'available') {
      result.push('is-disabled')
    }

    if (moment(date).isBefore(this.minDate) || moment(date).isAfter(this.maxDate)) {
      result.push('is-disabled')
    }

    return result
  }

  get disabledDates(): Date[] {
    return this.displayedDates
      .filter((date) => 'available' !== this.getDateStatus(date))
      .map((moment) => moment.toDate())
  }

  /**
   * A map of styles to messages. Each style can have multiple messages.
   */
  get uniqueAnnotations(): Array<[string, string[]]> {
    const results: Dict<Set<string>> = {}
    for (const date of this.displayedDates) {
      for (const annotation of this.getDateAnnotations(date)) {
        results[annotation.style] ??= new Set()
        results[annotation.style].add(annotation.message)
      }
    }
    return Object.entries(results).map(([style, messages]) => [style, Array.from(messages)])
  }

  handleFromPageChange(v) {
    // Workaround for an infinite loop bug.
    // When `minDate` and `maxDate` are in the same month and the multi-month view is active,
    // setting the `visibleFromDate` and `visibleToDate` to the first day of month of `@update:from-page`'s payload
    // and last day of the month of `@update:to-page`'s payload will get v-calendar to fire the aforementioned events repeatedly.
    if (this.minDate.isSame(this.maxDate, 'month')) {
      this.visibleFromDate = this.minDate.clone().startOf('month').valueOf()
    } else {
      this.visibleFromDate = moment({ year: v.year, month: v.month - 1 }).valueOf()
    }
  }

  handleToPageChange(v) {
    // See comment in the `handleFromPageChange()`
    if (this.minDate.isSame(this.maxDate, 'month')) {
      this.visibleToDate = this.maxDate.clone().endOf('month').valueOf()
    } else {
      this.visibleToDate = moment({ year: v.year, month: v.month - 1 })
        .endOf('month')
        .valueOf()
    }
  }

  handleDayClick(day) {
    if (!day.isDisabled) {
      this.onSelect(moment(day.date))
    }
  }

  handleDayMouseDown(day) {
    this.focusedDate = day
  }

  isDateSelected(date: Moment): boolean {
    return this.selectedDate ? moment(this.selectedDate)?.isSame(date, 'day') : false
  }

  tooltip(day): string {
    // TODO Do we need to specify TZ here?
    const date = moment(day.date)
    const labels: string[] = []

    if (moment().isSame(date, 'day')) {
      labels.push('Today')
    }

    if (this.isDateSelected(date)) {
      labels.push('Selected date')
    }

    if (this.isSoldOut(date)) {
      labels.push('Sold out')
    }

    const annotations = this.getDateAnnotations(moment(date))
    if (annotations.length > 0) {
      labels.push(...annotations.map((annotation) => annotation.message))
    }

    return labels.join('. ')
  }

  ariaLabel(day): string {
    const result: string[] = []
    result.push(day.ariaLabel)

    const tooltip = this.tooltip(day)
    if (tooltip) {
      result.push(tooltip)
    }
    return result.join('. ')
  }

  get shouldShowLegend(): boolean {
    if (Object.keys(this.uniqueAnnotations).length > 0) {
      return true
    }
    return this.displayedDates.some((date) => 'sold_out' === this.getDateStatus(date))
  }
}
